import { Button } from '@florencelabs/button';
import clsx from 'clsx';
import { ReactElement, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';

export interface ProviderSearchFiltersContextValue {
  isLoading: boolean;
  totalCount: number;
  resultsCountOverride?: number;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  setResultsCountOverride?: (count?: number) => void;
  registerOnShowResults?: (handler: () => void) => () => void;
  notifyShowResults?: () => void;
}

export const SearchFiltersContext = createContext<
  ProviderSearchFiltersContextValue | undefined
>(undefined);

export const useSearchFilters = (): ProviderSearchFiltersContextValue => {
  const context = useContext(SearchFiltersContext);
  if (context === undefined) {
    throw new Error(
      'useSearchFilters must be used within a ProviderSearchFiltersContext',
    );
  }
  return context;
};

export const FilterDrawerFooter = ({
  onClear,
  isClearDisabled,
  onShowResults: onShowResultsExternal,
  resultsText,
  resultsCount,
}: {
  onClear?: () => void;
  onShowResults?: () => void;
  isClearDisabled?: boolean;
  resultsText?: string;
  resultsCount?: number;
}): ReactElement => {
  const [t] = useTranslation();
  const {
    setIsOpen,
    isLoading,
    totalCount,
    resultsCountOverride,
    notifyShowResults,
  } = useSearchFilters();
  const countToDisplay = resultsCount ?? resultsCountOverride ?? totalCount;

  const onShowResults = () => {
    if (onShowResultsExternal) {
      onShowResultsExternal();
    }
    notifyShowResults?.();
    setIsOpen(false);
  };

  return (
    <div className={clsx('flex h-full items-center')}>
      <Button
        slot={null}
        className={'h-full w-full !rounded-none'}
        variant={'clientMain'}
        onPress={onClear}
        isDisabled={!onClear || isClearDisabled}
      >
        {t('clear')}
      </Button>
      <div className={'flex h-full w-[1px] items-center bg-primary-color'}>
        <div className={'h-8 w-[1px] shrink-0 bg-white opacity-20'} />
      </div>
      <Button
        slot={null}
        onPress={onShowResults}
        className={'h-full w-full !rounded-none'}
        variant={'clientMain'}
        isLoading={isLoading}
      >
        {t(resultsText ?? 'searchFilters.showResultsCount', {
          count: countToDisplay,
        })}
      </Button>
    </div>
  );
};
